import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const CALLOUTS = [{
  phase: 'Launch',
  title: 'Novelty — long format',
  desc: 'When a treatment is new, HCPs have both curiosity and a professional responsibility to stay informed. That creates room for longer-form education and longer intervals between exposures.',
  surface: 'border-[var(--accent)]/35 bg-[var(--accent)]/[0.07]',
  label: 'text-[var(--accent)]'
}, {
  phase: 'Established Brands',
  title: 'Short & frequent',
  desc: 'As a treatment becomes an Established Brand, that learning imperative fades. Communication therefore needs to become shorter, sharper and more frequent.',
  surface: 'border-[var(--brand)]/30 bg-[var(--brand)]/[0.06]',
  label: 'text-[var(--brand)]'
}] as const;
const MAIN_CURVE = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
const FORMAT_LINE = 'M 380 78 C 470 96 545 118 615 148 C 700 186 800 224 950 248';
const FREQUENCY_LINE = 'M 380 248 C 500 238 585 214 675 172 C 765 130 855 100 950 78';
const PHASES = [{
  label: 'R&D',
  sub: 'Investment & clinical trials',
  x: 230
}, {
  label: 'Launch',
  sub: 'Heavy marketing spend',
  x: 420
}, {
  label: 'Mature',
  sub: 'Peak revenue & share',
  x: 595
}, {
  label: 'Established Brands',
  sub: 'Loss of exclusivity',
  x: 845,
  accent: true
}];
const AXIS = '#D8DEE7';
export function SlideFormatByPhase({}: SlideProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [setTimeout(() => setStage(1), 400), setTimeout(() => setStage(2), 1300), setTimeout(() => setStage(3), 2600), setTimeout(() => setStage(4), 3800)];
    return () => timers.forEach(clearTimeout);
  }, []);
  return <InteriorSlide surface data-id="element-229" data-mp="illwe3j8cxzg">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="text-center flex-shrink-0" data-id="element-230" data-mp="11ph15rx8ahln">
        <p className="text-[var(--brand)] text-[clamp(0.7rem,1vw,0.9rem)] font-semibold uppercase tracking-[0.18em] mb-2" data-id="element-231" data-mp="xnvitr1xdb42">
          Format & Frequency
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.75rem,3.2vw,2.9rem)] leading-tight tracking-tight" data-id="element-232" data-mp="1le7wzjnrk433">
          As familiarity grows, format shortens
        </h2>
        <p className="text-[var(--muted)] text-[clamp(0.85rem,1.2vw,1.05rem)] max-w-2xl mx-auto mt-3" data-id="element-233" data-mp="1beh23gvajn2h">
          New treatments create a reason to learn. Established Brands need a
          reason to re-engage.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center py-2" data-id="element-234" data-mp="2ga7t34riii">
        <div className="w-full max-w-5xl max-h-full relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-235" data-mp="4x3o6y0icuiu">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-236" data-mp="3omrsvg2o94aq">
            {/* Axis annotations */}
            <text x="60" y="100" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-237" data-mp="6f2e9q6982pg">
              +
            </text>
            <text x="60" y="385" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-238" data-mp="39xc2i171z0vk">
              −
            </text>
            <text x="30" y="240" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} data-id="element-239" data-mp="3joqbr0ot3f1w">
              Cumulative Value
            </text>
            <text x="520" y="455" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" data-id="element-240" data-mp="2zvjhr4zq7zp2">
              Time
            </text>
            <text x="975" y="295" className="text-[10px] font-medium" fill="#9CA3AF" data-id="element-241" data-mp="39r3a5hf30g92">
              €0
            </text>

            {/* Axes */}
            <line x1="80" y1="40" x2="80" y2="430" stroke={AXIS} strokeWidth="2" data-id="element-242" data-mp="3il82645ybxq6" />
            <line x1="80" y1="290" x2="970" y2="290" stroke={AXIS} strokeWidth="2" data-id="element-243" data-mp="3njj843t4lo34" />

            {/* Phase dividers */}
            {[380, 460, 730].map(x => <line key={x} x1={x} y1="50" x2={x} y2="430" stroke={AXIS} strokeWidth="1" strokeDasharray="4,4" data-id="element-244" data-mp="1btear1sc79q" />)}

            {/* Product journey curve */}
            <path d={MAIN_CURVE} fill="none" stroke="#1A1A1A" strokeWidth="3" data-id="element-245" data-mp="kaxzfakub62c" />

            {/* Format length — long to short */}
            <motion.path d={FORMAT_LINE} fill="none" stroke="#0F9F9A" strokeWidth="3" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 0.9,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-246" data-mp="bg9rtdrslcad" />
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 0.25,
            delay: 0.6
          }} data-id="element-247" data-mp="u8u7tume7mwr">
              <text x="380" y="62" textAnchor="middle" className="text-xs font-semibold" fill="#0F9F9A" data-id="element-248" data-mp="3a0i3zntl1bei">
                Long
              </text>
              <text x="958" y="252" className="text-xs font-semibold" fill="#0F9F9A" data-id="element-249" data-mp="32ffkg5b98acp">
                Short
              </text>
              <text x="958" y="266" className="text-[10px]" fill="#0B7F7B" data-id="element-250" data-mp="37c60wtqz17yn">
                Content length
              </text>
            </motion.g>

            {/* Frequency & share of voice — rising */}
            <motion.path d={FREQUENCY_LINE} fill="none" stroke="#3157D5" strokeWidth="3" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.9,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-251" data-mp="2xgule97qnwpu" />
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.25,
            delay: 0.7
          }} data-id="element-252" data-mp="1v41jg3c1bpu1">
              <text x="958" y="75" className="text-xs font-semibold" fill="#3157D5" data-id="element-253" data-mp="201jubz9m4f4o">
                Frequency
              </text>
              <text x="958" y="89" className="text-xs font-semibold" fill="#3157D5" data-id="element-254" data-mp="2e01clhgr0zf7">
                &amp; SOV
              </text>
            </motion.g>

            {/* Phase labels */}
            {PHASES.map(p => <g key={p.label} data-id="element-255" data-mp="2isum0in5wvxm">
                <text x={p.x} y="450" textAnchor="middle" className="text-sm font-semibold" fill={p.accent ? '#3157D5' : '#374151'} data-id="element-256" data-mp="2itvtye8vt2b4">
                  {p.label}
                </text>
                <text x={p.x} y="468" textAnchor="middle" className="text-xs" fill={p.accent ? '#5B76DE' : '#9CA3AF'} data-id="element-257" data-mp="31dgyoaibo5v">
                  {p.sub}
                </text>
              </g>)}
            <text x="230" y="410" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-258" data-mp="gzbuxskx1iku">
              Investment
            </text>
            <text x="595" y="55" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-259" data-mp="3leth22x31fd8">
              Peak Revenue
            </text>
          </svg>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 12
    }} transition={{
      duration: 0.4,
      ease: [0.23, 1, 0.32, 1]
    }} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto w-full flex-shrink-0 mt-4" data-id="element-260" data-mp="2xnxtjhznt6bs">
        {CALLOUTS.map(c => <div key={c.phase} className={`rounded-2xl border p-4 ${c.surface}`} data-id="element-261" data-mp="2l4tvlruuou4u">
            <div className="flex items-baseline gap-2 mb-1.5" data-id="element-262" data-mp="2g6iin899e3ie">
              <span className={`text-[10px] font-bold uppercase tracking-wider ${c.label}`} data-id="element-263" data-mp="2b7xjzd9nnrw2">
                {c.phase}
              </span>
              <h3 className="text-[var(--fg)] font-bold text-sm" data-id="element-264" data-mp="26fe7kjmpf2su">{c.title}</h3>
            </div>
            <p className="text-[var(--muted)] text-[13px] leading-snug" data-id="element-265" data-mp="21gbbhoopc6x0">
              {c.desc}
            </p>
          </div>)}
      </motion.div>
    </InteriorSlide>;
}